'use client'

import React, { useEffect, useRef } from 'react'
import { useLanguage } from '../../providers/LanguageProvider'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import clsx from 'clsx'

gsap.registerPlugin(ScrollTrigger)

type ListBlockProps = {
  title: {
    en: string
    ar: string
  }
  items: {
    en: string[]
    ar: string[]
  }
}

const ListBlock: React.FC<ListBlockProps> = ({ title, items }) => {
  const { language } = useLanguage()
  const isArabic = language === 'ar'
  const containerRef = useRef<HTMLDivElement>(null)

  const listItems = isArabic ? items.ar : items.en
  const displayTitle = isArabic ? title.ar : title.en

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.who-animate', {
        y: 30,
        opacity: 0,
        stagger: 0.15,
        duration: 0.3,
        delay: 0.5,
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 40%',
          once: true,
        },
      })

      gsap.from('.who-title', {
        y: 30,
        opacity: 0,
        duration: 0.3,
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 45%',
          once: true,
        },
      })
    }, containerRef)

    return () => ctx.revert()
  }, [language])

  return (
    <section
      ref={containerRef}
      className="w-full bg-white text-black flex items-center py-12 sm:py-16 lg:pt-[90px] lg:pb-[190px] 2xl:py-36"
    >
      <div
        className={clsx(
          'section-container mx-auto flex flex-col sm:items-center',
          'gap-8 sm:gap-10 md:gap-[50px] lg:gap-[60px] xl:gap-[90px] 2xl:gap-[120px]',
        )}
      >
        {/* Title */}
        <h1
          className={clsx(
            'who-title font-medium text-gray-700 sm:text-center',
            isArabic
              ? 'text-[24px] sm:text-[28px] md:text-[32px] xl:text-[38px]'
              : 'text-[24px] sm:text-[28px] md:text-[32px] xl:text-[38px]',
          )}
        >
          {displayTitle}
        </h1>

        {/* List items */}
        <div
          className={clsx(
            'flex flex-col sm:flex-row text-black',
            'gap-6 sm:gap-[20px] md:gap-[30px] lg:gap-[40px] xl:gap-[114px] 2xl:gap-[120px]',
            'sm:px-6 md:px-0',
          )}
        >
          <ul className="flex flex-col gap-6 lg:gap-[38px]">
            {listItems.slice(0, 3).map((item, index) => (
              <li
                key={index}
                className={clsx(
                  'who-animate flex gap-2 items-start',
                  'text-[15px] sm:text-[17px] md:text-[20px] 2xl:text-[22px]',
                )}
              >
                <img src="/icons/symbol.png" alt="symbol" className="size-6 lg:size-8 md:mt-1 lg:mt-0" />
                {item}
              </li>
            ))}
          </ul>

          <ul className="flex flex-col gap-6 lg:gap-[38px]">
            {listItems.slice(3).map((item, index) => (
              <li
                key={index + 3}
                className={clsx(
                  'who-animate flex gap-2 items-start',
                  'text-[15px] sm:text-[17px] md:text-[20px] 2xl:text-[22px]',
                )}
              >
                <img src="/icons/symbol.png" alt="symbol" className="size-6 lg:size-8 md:mt-1 lg:mt-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

export default ListBlock
